import * as i0 from '@angular/core';
import { forwardRef, Directive, Input, HostListener, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import IMask from 'imask';
import { toString } from 'lodash-es';

class FsMaskDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        this.maskEnabled = true;
        this.format = 'number';
        this.padFractionalZeros = false;
        this.thousandsSeparator = ',';
        this.radix = '.';
        this.placeholderChar = '_';
        this.maskOptions = {};
    }
    blur() {
        setTimeout(() => {
            this._onTouched();
        });
    }
    input() {
        setTimeout(() => {
            this._onChange(this._controlValue);
        });
    }
    get imask() {
        return this._imask;
    }
    get _controlValue() {
        return this.maskEnabled
            ? this._imask.unmaskedValue
            : this._elementRef.nativeElement.value;
    }
    ngOnChanges(changes) {
        if (changes.maskEnabled) {
            this.maskEnabled = coerceBooleanProperty(this.maskEnabled);
            if (!changes.maskEnabled.firstChange
                && changes.maskEnabled.previousValue !== changes.maskEnabled.currentValue) {
                this._updateMaskState();
            }
        }
    }
    ngOnInit() {
        if (this.format === 'currency') {
            this.mask = '$num';
            this.padFractionalZeros = true;
            this.signed = this.signed ?? false;
            this.scale = this.scale ?? 2;
            this.blocks = {
                num: {
                    mask: Number,
                    thousandsSeparator: ',',
                    radix: '.',
                    mapToRadix: [','],
                },
            };
        }
        this._updateMaskState();
    }
    ngAfterContentInit() {
        this._focusHack();
    }
    ngOnDestroy() {
        this._destroyMask();
    }
    writeValue(value) {
        if (this._imask) {
            this._imask.value = toString(value);
        }
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    _updateMaskState() {
        if (coerceBooleanProperty(this.maskEnabled)) {
            this._initMask();
        }
        else {
            this._destroyMask();
        }
    }
    _initMask() {
        if (!!this._imask) {
            return;
        }
        const maskOptions = {
            mask: this.mask,
            radix: this.radix,
            thousandsSeparator: this.thousandsSeparator,
            padFractionalZeros: this.padFractionalZeros,
            scale: this.scale,
            signed: this.signed,
            min: this.min,
            lazy: this.lazy,
            blocks: this.blocks,
            max: this.max,
            placeholderChar: this.placeholderChar,
            startsWith: this.startsWith,
            ...this.maskOptions,
        };
        this._imask = IMask(this._elementRef.nativeElement, maskOptions);
    }
    _destroyMask() {
        if (!!this._imask) {
            this._imask.destroy();
            this._imask = null;
        }
    }
    /**
     * This issue related with A-T2185
     *
     * After modal dialog opened - material dialog do focus and cursor pointer
     * is before the text. Its somehow related with imask and material.
     * That hack should work for it
     */
    _focusHack() {
        const focusCallback = () => {
            this._elementRef.nativeElement.setSelectionRange(this._elementRef.nativeElement.value.length, this._elementRef.nativeElement.value.length);
        };
        setTimeout(() => {
            this._elementRef.nativeElement.removeEventListener('focus', focusCallback);
        }, 1000);
        this._elementRef.nativeElement.addEventListener('focus', focusCallback);
    }
}
FsMaskDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsMaskDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsMaskDirective, selector: "[fsMask]", inputs: { maskEnabled: ["fsMask", "maskEnabled"], format: "format", mask: "mask", padFractionalZeros: "padFractionalZeros", thousandsSeparator: "thousandsSeparator", radix: "radix", scale: "scale", signed: "signed", min: "min", max: "max", startsWith: "startsWith", lazy: "lazy", placeholderChar: "placeholderChar", blocks: "blocks", maskOptions: "maskOptions" }, host: { listeners: { "blur": "blur()", "input": "input()" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsMaskDirective),
            multi: true,
        }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsMask]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsMaskDirective),
                            multi: true,
                        }],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { maskEnabled: [{
                type: Input,
                args: ['fsMask']
            }], format: [{
                type: Input
            }], mask: [{
                type: Input
            }], padFractionalZeros: [{
                type: Input
            }], thousandsSeparator: [{
                type: Input
            }], radix: [{
                type: Input
            }], scale: [{
                type: Input
            }], signed: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], startsWith: [{
                type: Input
            }], lazy: [{
                type: Input
            }], placeholderChar: [{
                type: Input
            }], blocks: [{
                type: Input
            }], maskOptions: [{
                type: Input
            }], blur: [{
                type: HostListener,
                args: ['blur']
            }], input: [{
                type: HostListener,
                args: ['input']
            }] } });

class FsMaskNumberDirective extends FsMaskDirective {
    constructor() {
        super(...arguments);
        this.mask = Number;
    }
}
FsMaskNumberDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskNumberDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FsMaskNumberDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsMaskNumberDirective, selector: "[fsMaskNumber]", providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsMaskNumberDirective),
            multi: true,
        }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskNumberDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsMaskNumber]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsMaskNumberDirective),
                            multi: true,
                        }],
                }]
        }] });

class FsMaskRexExpDirective extends FsMaskDirective {
    set setMask(value) {
        const matches = value.match(/^\/(.*)\/([a-z]*)$/);
        this.mask = new RegExp(matches[1], matches[2]);
    }
}
FsMaskRexExpDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskRexExpDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FsMaskRexExpDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsMaskRexExpDirective, selector: "[fsMaskRegExp]", inputs: { setMask: ["mask", "setMask"] }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsMaskRexExpDirective),
            multi: true,
        }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskRexExpDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsMaskRegExp]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsMaskRexExpDirective),
                            multi: true,
                        }],
                }]
        }], propDecorators: { setMask: [{
                type: Input,
                args: ['mask']
            }] } });

class FsMaskCurrencyDirective extends FsMaskDirective {
    constructor() {
        super(...arguments);
        this.format = 'currency';
    }
}
FsMaskCurrencyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskCurrencyDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FsMaskCurrencyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsMaskCurrencyDirective, selector: "[fsMaskCurrency]", providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsMaskCurrencyDirective),
            multi: true,
        }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskCurrencyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsMaskCurrency]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsMaskCurrencyDirective),
                            multi: true,
                        }],
                }]
        }] });

class FsMaskModule {
    static forRoot() {
        return {
            ngModule: FsMaskModule
        };
    }
}
FsMaskModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsMaskModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskModule, declarations: [FsMaskDirective,
        FsMaskNumberDirective,
        FsMaskRexExpDirective,
        FsMaskCurrencyDirective], imports: [CommonModule], exports: [FsMaskDirective,
        FsMaskNumberDirective,
        FsMaskRexExpDirective,
        FsMaskCurrencyDirective] });
FsMaskModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskModule, imports: [[
            CommonModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsMaskModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        FsMaskDirective,
                        FsMaskNumberDirective,
                        FsMaskRexExpDirective,
                        FsMaskCurrencyDirective
                    ],
                    declarations: [
                        FsMaskDirective,
                        FsMaskNumberDirective,
                        FsMaskRexExpDirective,
                        FsMaskCurrencyDirective,
                    ],
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FsMaskCurrencyDirective, FsMaskDirective, FsMaskModule, FsMaskNumberDirective, FsMaskRexExpDirective };
